import { Controller } from 'stimulus'

const SIGNED_OUT = 'You were signed out. Sign in again in another tab, then tap Save count.'

// Verify inventory on the phone (app/views/mobile/inventory/verify): each item
// in the location is confirmed ("8 L is right") or counted ("Count differs",
// which posts the count to InventoryController#count and records the
// difference as usage). LIMSey keeps no "verified" mark, as on the desktop's
// Verify Inventory, so what's been checked lives in this tab's
// sessionStorage for the day, keyed by account and location, and the bar shows
// how far through the location you are.
export default class extends Controller {
  static targets = ['row', 'progress', 'bar']

  connect() {
    this.checked = new Set(this.load())
    this.rowTargets.forEach(row => this.paint(row))
    this.update()
  }

  confirm(event) {
    this.check(this.rowOf(event), true)
  }

  undo(event) {
    this.check(this.rowOf(event), false)
  }

  differs(event) {
    const row = this.rowOf(event)
    const form = row.querySelector('[data-role="adjust"]')
    form.hidden = !form.hidden
    if (!form.hidden) form.querySelector('input').focus()
  }

  save(event) {
    event.preventDefault()
    const form = event.currentTarget
    const row = form.closest('[data-target~="mobile-inventory-verify.row"]')
    const input = form.querySelector('input[name="counted"]')
    const button = form.querySelector('[type="submit"]')
    const error = form.querySelector('[data-role="error"]')
    const token = document.querySelector('meta[name="csrf-token"]')
    button.disabled = true
    error.hidden = true

    fetch(row.dataset.url, {
      method: 'POST',
      credentials: 'same-origin',
      redirect: 'manual',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': token ? token.content : ''
      },
      body: JSON.stringify({ counted: input.value })
    }).then((response) => {
      if (response.type === 'opaqueredirect' || response.redirected) return { ok: false, data: { errors: [SIGNED_OUT] } }
      return response.json().catch(() => ({})).then(data => ({ ok: response.ok, data }))
    }).then(({ ok, data }) => {
      if (!ok) throw new Error((data.errors || [data.message || 'The count was not saved. Try again.']).join(' '))

      row.querySelector('[data-role="qty"]').textContent = data.label
      const done = row.querySelector('[data-role="done"]')
      done.textContent = `Count saved: ${data.label} now.`
      done.hidden = false
      form.hidden = true
      form.reset()
      this.check(row, true)
    }).catch((err) => {
      error.textContent = err instanceof TypeError ? 'No connection. Tap Save count again.' : err.message
      error.hidden = false
    })
      .finally(() => { button.disabled = false })
  }

  // --- state --------------------------------------------------------------

  rowOf(event) {
    return event.currentTarget.closest('[data-target~="mobile-inventory-verify.row"]')
  }

  check(row, on) {
    const id = row.dataset.itemId
    if (on) { this.checked.add(id) } else { this.checked.delete(id) }
    this.store()
    this.paint(row)
    this.update()
  }

  paint(row) {
    const on = this.checked.has(row.dataset.itemId)
    row.classList.toggle('is-ok', on)
    const acts = row.querySelector('[data-role="acts"]')
    const badge = row.querySelector('.mb-inv-vok')
    acts.hidden = on
    badge.hidden = !on
    const form = row.querySelector('[data-role="adjust"]')
    if (form && on) form.hidden = true
  }

  update() {
    const total = this.rowTargets.length
    const done = this.rowTargets.filter(row => this.checked.has(row.dataset.itemId)).length
    if (this.hasProgressTarget) this.progressTarget.textContent = `${done} of ${total}`
    if (this.hasBarTarget) {
      this.barTarget.style.width = total ? `${Math.round((done * 100) / total)}%` : '0%'
      this.barTarget.parentElement.setAttribute('aria-valuenow', done)
    }
  }

  load() {
    try {
      return JSON.parse(window.sessionStorage.getItem(this.data.get('key')) || '[]')
    } catch (e) {
      return []
    }
  }

  store() {
    try {
      window.sessionStorage.setItem(this.data.get('key'), JSON.stringify(Array.from(this.checked)))
    } catch (e) {
      // Private browsing or storage off: the marks last until the page reloads.
    }
  }
}
